/* eslint-disable jsx-a11y/media-has-caption */
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { CSS } from '@dnd-kit/utilities'
import { useEffect, useRef, useState } from 'react'
import { useSortable } from '@dnd-kit/sortable'

import {
  Box,
  Icon,
  Image,
  LoadingSpinner,
  Text,
  Textarea,
  checkIfIOS,
  CheckboxControl,
} from '@redriverpress/system'

import { useTextToSpeech } from 'Packages/tts'

import { modePropType } from './propTypes'

const titleMaxLength = 80

const CircularButton = styled.button`
  appearance: none;
  border: none;
  border-radius: ${props => props.theme.radii.circular};
  background: ${props => props.theme.colors.gray150};
  color: ${props => props.theme.colors.black};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2rem;
  width: 2rem;
  padding: 0;

  &:hover,
  &:focus {
    background: ${props => props.theme.colors.blue500};
    color: ${props => props.theme.colors.white};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.theme.colors.white},
      0 0 0 4px ${props => props.theme.colors.blue500};
  }
`

/** @redriverpress/system checkbox requires a label, we don't. */
function Checkbox({ disabled, ...props }) {
  return (
    <Text
      as='label'
      cursor={disabled ? 'not-allowed' : 'pointer'}
      display='inline-block'
    >
      <CheckboxControl {...props} />
    </Text>
  )
}

export default function UserFlashcard({
  id,
  mode,
  onRemove,
  onRename,
  thumbnail,
  title,
  userTitle,
  selectable,
  selected,
  onSelectionChange,
}) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id, disabled: mode !== 'reorder' })

  let cursor = 'auto'
  if (mode === 'reorder') cursor = isDragging ? 'grabbing' : 'grab'

  const dragStyle = {
    cursor,
    // https://docs.dndkit.com/api-documentation/sensors/touch#recommendations
    touchAction: isDragging ? 'manipulation' : 'auto',
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const audioNode = useRef(null)
  const { data: ttsData } = useTextToSpeech(userTitle)

  // See implementation notes in SimpleAudio.js
  const [canPlayThrough, setCanPlayThrough] = useState(false)
  const [isWaiting, setIsWaiting] = useState(false)

  useEffect(() => {
    setCanPlayThrough(checkIfIOS())
  }, [])

  // The working title is a working copy of the userTitle.
  // We need this in a state hook so we don't trigger a component rerender for every keypress on the textarea.
  const [workingTitle, setWorkingTitle] = useState(userTitle)

  // Update the userTitle when the textarea blurs
  const handleBlur = e => {
    if (e.target.value === '') {
      // If the working title is erased, reset it (and the userTitle) to the flashcard's default title
      setWorkingTitle(title)
      onRename({ id, value: title })
    } else {
      // Otherwise, set the userTitle to whatever the working title is
      onRename({ id, value: workingTitle })
    }
  }

  const [removalPending, setRemovalPending] = useState(false)
  const handleRemoval = () => {
    setRemovalPending(true)
    onRemove(id)
  }

  return (
    <Box
      {...attributes}
      {...listeners}
      ref={setNodeRef}
      style={dragStyle}
      bg='white'
      borderRadius='rounded'
      p={3}
      position='relative'
      opacity={isDragging ? 0.5 : 1}
    >
      {mode === 'edit' && selectable && (
        <Box position='absolute' top={2} right={2}>
          <Checkbox
            name='flashcard-selection'
            small
            checked={selected}
            onChange={() => onSelectionChange(id)}
            value={id}
          />
        </Box>
      )}
      {mode === 'reorder' && (
        <Box position='absolute' top={1} right={1}>
          <Icon weight='regular' icon='ellipsis-v' pr='2px' />
          <Icon weight='regular' icon='ellipsis-v' />
        </Box>
      )}
      <Image
        height={100}
        width={100}
        display='block'
        mx='auto'
        src={thumbnail}
        alt=''
      />
      {(mode === 'default' || mode === 'reorder') && (
        <Box display='flex' flexDirection='column' alignItems='center'>
          <Text py={2} textAlign='center' fontWeight={600}>
            {userTitle}
          </Text>
          {mode === 'default' && (
            <>
              <audio
                onCanPlayThrough={() => setCanPlayThrough(true)}
                onPlaying={() => setIsWaiting(false)}
                onWaiting={() => setIsWaiting(true)}
                preload='auto'
                ref={audioNode}
                src={ttsData?.audio_url}
              />
              <CircularButton
                disabled={!(canPlayThrough && !isWaiting)}
                onClick={() => audioNode.current.play()}
              >
                {ttsData?.audio_url && canPlayThrough && !isWaiting ? (
                  <Icon
                    icon='play'
                    fontSize={0}
                    position='relative'
                    top='1px'
                    left='1px'
                  />
                ) : (
                  <LoadingSpinner fontSize={0} />
                )}
              </CircularButton>
            </>
          )}
        </Box>
      )}

      {mode === 'edit' && (
        <>
          <Textarea
            width={1}
            rows={2}
            placeholder={title}
            maxLength={titleMaxLength}
            value={workingTitle}
            onChange={e => setWorkingTitle(e.target.value)}
            onBlur={handleBlur}
            disabled={removalPending}
          />
          <Text
            textAlign='right'
            color={workingTitle.length < titleMaxLength ? 'gray700' : 'red700'}
          >
            {workingTitle.length}/{titleMaxLength}
          </Text>
          <Box display='flex' justifyContent='center'>
            <CircularButton disabled={removalPending} onClick={handleRemoval}>
              {removalPending ? <LoadingSpinner /> : <Icon icon='trash-alt' />}
            </CircularButton>
          </Box>
        </>
      )}
    </Box>
  )
}

UserFlashcard.propTypes = {
  id: PropTypes.number.isRequired,
  mode: modePropType.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  thumbnail: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  userTitle: PropTypes.string.isRequired,
  selectable: PropTypes.bool.isRequired,
  selected: PropTypes.bool.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
}
